import { useRef } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { colors } from '@kangaroo/ui';

import { AuthAvatar } from '../AuthAvatar';

type UserAvatarUploadProps = {
  name: string;
  initials: string;
  avatarUrl: string | null;
  uploading?: boolean;
  onPickFile: (file: File) => void;
};

export function UserAvatarUpload({
  name,
  initials,
  avatarUrl,
  uploading = false,
  onPickFile,
}: UserAvatarUploadProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const hasImage = Boolean(avatarUrl);

  function handleFiles(files: FileList | null) {
    const file = files?.[0];
    if (file) {
      onPickFile(file);
    }
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
        gap: 2,
        alignItems: 'center',
        p: 1.4,
        borderRadius: 1.2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'subtleFill.main',
      }}
    >
      <Box sx={{ position: 'relative', width: 96, height: 96, mx: { xs: 'auto', sm: 0 } }}>
        <AuthAvatar
          src={avatarUrl}
          alt={`${name} avatar`}
          sx={{
            width: 96,
            height: 96,
            fontSize: '1.35rem',
            fontWeight: 900,
            bgcolor: '#2B1B34',
            color: '#FFFFFF',
            border: '1px solid rgba(255,255,255,0.18)',
          }}
        >
          {initials}
        </AuthAvatar>
        {uploading ? (
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(15,23,42,0.55)', borderRadius: '50%' }}>
            <CircularProgress size={28} sx={{ color: colors.primary[300] }} />
          </Box>
        ) : null}
      </Box>

      <Stack spacing={1.1}>
        <Stack spacing={0.35}>
          <Typography sx={{ fontSize: '0.88rem', fontWeight: 850 }}>Profile photo</Typography>
          <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', lineHeight: 1.45 }}>
            Upload a photo for your sidebar profile and team views. JPG, PNG, or WebP up to 5 MB.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant={hasImage ? 'outlined' : 'contained'}
            disabled={uploading}
            startIcon={uploading ? undefined : <PhotoLibraryOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ textTransform: 'none', fontSize: '0.74rem', fontWeight: 700 }}
          >
            {uploading ? 'Uploading...' : hasImage ? 'Replace photo' : 'Choose file'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={uploading}
            startIcon={<PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => cameraInputRef.current?.click()}
            sx={{ textTransform: 'none', fontSize: '0.74rem', fontWeight: 700 }}
          >
            Camera
          </Button>
        </Stack>
        {!hasImage ? (
          <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', color: 'text.secondary' }}>
            <CloudUploadOutlinedIcon sx={{ fontSize: 16, color: colors.primary[300] }} />
            <Typography sx={{ fontSize: '0.68rem' }}>No photo yet — your initials will show until you upload one.</Typography>
          </Stack>
        ) : null}
      </Stack>

      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={(event) => handleFiles(event.target.files)} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="user" hidden onChange={(event) => handleFiles(event.target.files)} />
    </Box>
  );
}
